import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetCommentsQuery,
  usePaginateFilterDiscussionsQuery,
} from "@/redux/service";
import { isPaginatedResponse } from "@/redux/typeGuards";
import type { PaginatedResponse } from "@/redux/types";
import type { ModerationState } from "@/redux/discussions/types";
import {
  CommunityActivityItem,
  UseCommunityActivityOptions,
} from "@/domains/certifications/types";
import { htmlToText } from "@/domains/certifications/utils";
import {
  MODERATION_STATES,
  UNDER_REVIEW_STATES,
} from "@/domains/moderation/constants";
import { CONTRIBUTION_KINDS } from "@/domains/certifications/constants";

const ACTIVITY_PAGE_SIZE = 50;
const RETIRED_STATES: ModerationState[] = [
  MODERATION_STATES.WITHDRAWN,
  MODERATION_STATES.SUPERSEDED,
];
const QUERY_OPTIONS = { refetchOnMountOrArgChange: true };

const listOf = <T>(response: T[] | PaginatedResponse<T> | undefined): T[] => {
  if (!response) {
    return [];
  }
  return isPaginatedResponse(response) ? response.results : response;
};

export const useCommunityActivity = ({
  topicIds,
  userId,
  includeRemoved = false,
}: UseCommunityActivityOptions) => {
  const canQuery = Boolean(topicIds?.length) && userId !== null;
  const posts = usePaginateFilterDiscussionsQuery(
    canQuery
      ? { topics: topicIds, authors: userId, pageSize: ACTIVITY_PAGE_SIZE }
      : skipToken,
    QUERY_OPTIONS,
  );
  const comments = useGetCommentsQuery(
    canQuery
      ? {
          createdBy: userId,
          topics: topicIds,
          ordering: "-created_at",
          pageSize: ACTIVITY_PAGE_SIZE,
        }
      : skipToken,
    QUERY_OPTIONS,
  );

  const items = useMemo<CommunityActivityItem[]>(() => {
    if (!canQuery) {
      return [];
    }
    const isShown = (state: ModerationState) =>
      !RETIRED_STATES.includes(state) &&
      (includeRemoved || state !== MODERATION_STATES.REJECTED);
    const postItems: CommunityActivityItem[] = listOf(posts.data)
      .filter((post) => isShown(post.moderationState))
      .map((post) => ({
        key: `post-${post.id}`,
        id: post.id,
        kind: CONTRIBUTION_KINDS.POST,
        moderationState: post.moderationState,
        discussionId: post.id,
        discussionTitle: post.title,
        text: htmlToText(post.content),
        createdAt: post.createdAt,
        likesCount: post.likesCount,
        commentsCount: post.commentsCount,
        isUnderReview: UNDER_REVIEW_STATES.includes(post.moderationState),
        topicIds: post.topics,
      }));
    const commentItems: CommunityActivityItem[] = listOf(comments.data)
      .filter(
        (comment) => !comment.isDeleted && isShown(comment.moderationState),
      )
      .map((comment) => ({
        key: `comment-${comment.id}`,
        id: comment.id,
        kind: CONTRIBUTION_KINDS.COMMENT,
        moderationState: comment.moderationState,
        discussionId: comment.discussion,
        discussionTitle: comment.discussionTitle,
        text: htmlToText(comment.content),
        createdAt: comment.createdAt,
        likesCount: comment.likesCount,
        commentsCount: null,
        isUnderReview: UNDER_REVIEW_STATES.includes(comment.moderationState),
        topicIds: comment.topics,
      }));

    return [...postItems, ...commentItems].sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
  }, [posts.data, comments.data, canQuery, includeRemoved]);

  return {
    items,
    isLoading: posts.isLoading || comments.isLoading,
    isError: posts.isError || comments.isError,
  };
};
